// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { IconDownload } from 'components/atoms/icons/IconDownload';
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import DOMPurify from 'dompurify';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSlaFormatYearMonthDate, getUtcFormatDateTime } from 'ts/formatDate';
import { DATE_FORMAT, DEFAULT_PAGE } from 'util/ConstantValues';
import {
  GetICCardScanHistoryParams,
  ICCardScanHistories,
  ICCardScanItem,
} from 'util/Types';
import styles from './ICCardScanHistory.module.scss';
import { ExpandableList } from 'components/organisms/ExpandableList';
import { getICCardScanHistories } from 'apis/ic_cards/ICCardApi';
import Csv from 'util/csv';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import CustomPagination from 'components/atoms/CustomPagination';
import { mergeClasses } from 'util/commons';

const RECORDS_OF_PAGE = 100;

const ICCardScanHistory = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<ICCardScanHistories>({
    page: DEFAULT_PAGE,
    total: RECORDS_OF_PAGE,
    scanIcCardHistories: [],
  });
  const [filter, setFilter] = useState<GetICCardScanHistoryParams>({});
  const isDisplayPagination = data.total > RECORDS_OF_PAGE;

  useEffect(() => {
    fetchICCardScanHistories();
  }, []);

  useEffect(() => {
    fetchICCardScanHistories(filter);
  }, [filter.page]);

  const fetchICCardScanHistories = (params?: GetICCardScanHistoryParams) => {
    getICCardScanHistories({
      params: {
        ...params,
        take: RECORDS_OF_PAGE,
        page: params?.page || DEFAULT_PAGE,
      },
      successCallback: (res) => {
        setData(res);
      },
    });
  };

  const csvHeader: (string | [string, string])[] = useMemo(() => {
    return [
      ['date', t('ic_card_scan_history.date')],
      ['scan_time', t('ic_card_scan_history.scan_time')],
      ['ic_code', t('ic_card_scan_history.ic_code')],
    ];
  }, [t]);

  const handleSubmitFilter = () => {
    setFilter((prev) => ({ ...prev, page: DEFAULT_PAGE }));
    fetchICCardScanHistories({ ...filter, page: DEFAULT_PAGE });
  };

  const handleDownload = (item: ICCardScanItem) => {
    const filename = `${t(
      'ic_card_scan_history.heading'
    )}_${getUtcFormatDateTime(item.scanAt, DATE_FORMAT.YMDHm)}`;

    new Csv({
      headers: csvHeader,
      data:
        item.cards.map((card) => ({
          date: getSlaFormatYearMonthDate(item.scanAt),
          scan_time: getUtcFormatDateTime(item.scanAt, DATE_FORMAT.Hms),
          ic_code: `="${card}"`,
        })) || [],
    }).downLoadCsv({ filename });
  };

  return (
    <PrimaryTemplate
      h1={t('ic_card_scan_history.heading')}
      className={styles.ICCardScanHistory}
    >
      <div className={styles.ICCardScanHistoryBar}>
        <div
          className={styles.countList}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              t('common.page', {
                page: data.total,
              }) ?? ''
            ),
          }}
        />

        <div className={styles.ICCardScanHistoryFilter}>
          <div className={styles.filterWrapper}>
            <div className={styles.ICCardScanHistoryFilterBox}>
              <TableFilterCalendar
                containerClassName={styles.DateRangePicker}
                expanded={{
                  placeholder: t('common.placeholder.select_date_range'),
                  icon: <IconCalendarTablet />,
                }}
                fullWidth
                allowClear={true}
                onChange={(values) => {
                  setFilter((prev) => ({
                    ...prev,
                    dateFrom: values?.[0]
                      ? getSlaFormatYearMonthDate(values[0])
                      : '',
                    dateTo: values?.[1]
                      ? getSlaFormatYearMonthDate(values[1])
                      : '',
                  }));
                }}
                value={[filter?.dateFrom || '', filter?.dateTo || '']}
                endDateRequired={true}
              />
            </div>
          </div>

          <button
            className={styles.buttonApply}
            onClick={() => handleSubmitFilter()}
          >
            {t('dashboard.button_apply')}
          </button>
        </div>
      </div>

      <div
        className={mergeClasses(styles.ICCardScanHistoryTable, {
          [styles.TableWithoutPagination]: !isDisplayPagination,
        })}
      >
        <table>
          <thead>
            <tr>
              <th className={styles.date}>{t('ic_card_scan_history.date')}</th>
              <th className={styles.scan_time}>
                {t('ic_card_scan_history.scan_time')}
              </th>
              <th className={styles.ic_code}>
                {t('ic_card_scan_history.ic_code')}
              </th>
              <th className={styles.buttonDownload}>
                {t('ic_card_scan_history.download')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.total > 0 ? (
              data.scanIcCardHistories.map((item, index) => {
                return (
                  <tr key={index}>
                    <td className={styles.date}>
                      {getSlaFormatYearMonthDate(item.scanAt)}
                    </td>
                    <td className={styles.scan_time}>
                      {getUtcFormatDateTime(item.scanAt, DATE_FORMAT.Hms)}
                    </td>
                    <td className={styles.ic_code}>
                      <ExpandableList data={item.cards} />
                    </td>
                    <td className={styles.buttonDownload}>
                      <button onClick={() => handleDownload(item)}>
                        <IconDownload />
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} className={styles.msgError}>
                  {t('messages.M_008')}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className={styles.pagination}>
          {isDisplayPagination && (
            <CustomPagination
              pageCount={Math.ceil(data.total / RECORDS_OF_PAGE)}
              onPageChange={({ selected }) => {
                setFilter((prev) => ({ ...prev, page: selected + 1 }));
              }}
            />
          )}
        </div>
      </div>
    </PrimaryTemplate>
  );
};

export default ICCardScanHistory;
